import { useEffect, useState } from 'react';
import { createRestaurantSection, updateRestaurantSection } from '../../services/restaurant/restaurantSectionService';
import { useTranslation } from 'react-i18next';
import { RestaurantSectionType, getRestaurantSectionTypeDisplayName, getAvailableRestaurantSectionTypes } from '../../types/RestaurantSectionType';

interface SaveRestaurantSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  restaurantId?: string;
  initialId?: string;
  initialName?: string;
  initialRestaurantSectionType?: RestaurantSectionType;
  onSave: () => Promise<void>;
}

export default function SaveRestaurantSectionModal({
  isOpen,
  onClose,
  mode,
  restaurantId,
  initialId,
  initialName,
  initialRestaurantSectionType,
  onSave,
}: SaveRestaurantSectionModalProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initialName || '');
  const [restaurantSectionType, setRestaurantSectionType] = useState<RestaurantSectionType>(
    initialRestaurantSectionType || RestaurantSectionType.FOOD
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialName || '');
      setRestaurantSectionType(initialRestaurantSectionType || RestaurantSectionType.FOOD);
      setError(null);
      setSaving(false);
    }
  }, [isOpen, initialName, initialRestaurantSectionType]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError(t('restaurantSections.nameRequired'));
      return;
    }

    if (!restaurantSectionType) {
      setError('Section type is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (mode === 'create') {
        if (!restaurantId) {
          setError(t('restaurantSections.restaurantIdRequired'));
          return;
        }
        await createRestaurantSection(restaurantId, name.trim(), restaurantSectionType);
      } else {
        if (!initialId) {
          setError(t('restaurantSections.sectionIdRequired'));
          return;
        }
        await updateRestaurantSection(initialId, name.trim(), restaurantSectionType);
      }
      await onSave();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(
        mode === 'create'
          ? t('restaurantSections.couldNotCreate')
          : t('restaurantSections.couldNotUpdate')
      );
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/50" onClick={onClose}>
      <div
        className="bg-card dark:bg-card-dark rounded-3xl shadow-2xl w-full max-w-md border border-zinc-200 dark:border-white/20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 dark:text-zinc-50">
              {mode === 'create' ? t('restaurantSections.addNewSection') : t('restaurantSections.editSection')}
            </h2>
            <button
              onClick={onClose}
              className="flex-none p-2 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition"
              aria-label={t('restaurantSections.close')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
              </svg>
            </button>
          </div>

          {/* Form */}
          <div className="space-y-5">
            <div>
              <label htmlFor="sectionName" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                {t('restaurantSections.sectionName')}
              </label>
              <input
                type="text"
                id="sectionName"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
                placeholder={t('restaurantSections.enterSectionName')}
              />
            </div>

            <div>
              <label htmlFor="restaurantSectionType" className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">
                Section Type
              </label>
              <select
                id="restaurantSectionType"
                value={restaurantSectionType}
                onChange={(e) => setRestaurantSectionType(e.target.value as RestaurantSectionType)}
                className="w-full px-4 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:focus:ring-emerald-500/20 focus:border-emerald-500 transition"
              >
                {getAvailableRestaurantSectionTypes().map(type => (
                  <option key={type} value={type}>
                    {getRestaurantSectionTypeDisplayName(type)}
                  </option>
                ))}
              </select>
            </div>

            {error && (
              <div className="p-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-sm text-red-600 dark:text-red-400">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition disabled:opacity-50"
              >
                {t('restaurantSections.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !name.trim() || !restaurantSectionType}
                className="flex-1 px-6 py-3 rounded-2xl text-base font-semibold bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 hover:bg-zinc-800 dark:hover:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving
                  ? mode === 'create'
                    ? t('restaurantSections.creating')
                    : t('restaurantSections.saving')
                  : mode === 'create'
                  ? t('restaurantSections.create')
                  : t('restaurantSections.saveChanges')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}